import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { crm } from './crmApi';
import type { CrmAuth, DeletedLead } from './crmApi';
import { Empty, Head, Spinner, fmtPhone, fmtWhen, inputCls, purpleBg } from './ui';

// ── Deleted leads ────────────────────────────────────────────
// Leads a Lead Manager, Sales Admin or the admin deleted (a wrong number, a
// duplicate, a test…). Each waits 30 days, when it can be restored, then it
// is gone for good. Its history stays, so the calls made on it still count on
// the Monitor. Mirrors the app's crm_deleted_screen.dart.

export const CrmDeletedLeads: React.FC<{ auth: CrmAuth; embedded?: boolean }> = ({ auth, embedded }) => {
  const [q, setQ] = useState('');
  const [data, setData] = useState<{ days: number; leads: DeletedLead[] } | null>(null);
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    try { setData(await crm.deletedLeads(auth, q.trim())); } catch (e: any) { toast.error(e.message); }
  }, [auth, q]);
  useEffect(() => {
    const t = setTimeout(load, q ? 300 : 0);
    return () => clearTimeout(t);
  }, [load, q]);

  const restore = async (d: DeletedLead) => {
    setBusy(d.id);
    try {
      const r = await crm.restoreLead(auth, d.id);
      toast.success(`Lead #${r.lead.leadNo} is back${r.lead.ownerName ? ` with ${r.lead.ownerName}` : ''}`);
      load();
    } catch (e: any) { toast.error(e.message); }
    finally { setBusy(''); }
  };

  return (
    <div className="space-y-4">
      <Head embedded={embedded} title="Deleted leads" sub={`Each stays ${data?.days || 30} days, when it can be restored, then it is gone for good. The calls made on them still count on the Monitor.`} />
      <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search name, phone or lead number" className={`${inputCls} max-w-sm`} aria-label="Search deleted leads" />
      {!data ? <Spinner /> : data.leads.length === 0 ? <Empty icon="🗑️" title={q ? 'None match' : 'No deleted leads'} sub="Leads deleted in the last 30 days show up here." /> : (
        <div className="grid gap-2 lg:grid-cols-2">
          {data.leads.map(d => (
            <div key={d.id} className="bg-white border border-gray-100 rounded-2xl px-4 py-3 space-y-1.5">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-bold text-gray-800">#{d.leadNo} {d.name || 'No name'} <span className="text-sm font-mono font-medium text-gray-500">{fmtPhone(d.phone)}</span></p>
                  <p className="text-xs text-gray-500">Was {d.stage}{d.ownerName ? ` with ${d.ownerName}` : ''}</p>
                </div>
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap ${d.daysLeft <= 5 ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'}`}>{d.daysLeft} day{d.daysLeft === 1 ? '' : 's'} left</span>
              </div>
              <p className="text-xs text-gray-600">Deleted by <b>{d.byName}</b> {fmtWhen(d.deletedAt)}{d.reason ? ` · ${d.reason}` : ''}</p>
              <button onClick={() => restore(d)} disabled={!!busy} className="px-3 py-1.5 rounded-lg text-[11px] font-bold text-white disabled:opacity-50" style={purpleBg}>{busy === d.id ? 'Restoring…' : '♻️ Restore'}</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CrmDeletedLeads;
